import React, { useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import OutlinedInput from "@mui/material/OutlinedInput";
import api from "../helpers/api";

interface ForgotPasswordProps {
  open: boolean;
  handleClose: () => void;
}

export default function ForgotPassword({
  open,
  handleClose,
}: ForgotPasswordProps) {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [success, setSuccess] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const validateEmail = (email: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateEmail(email)) {
      setIsValid(false);
      setErrorMessage("Please enter a valid email address");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/user/passwordreset", {
        email: email.trim().toLowerCase(),
      });
      if (res.status === 200) {
        setSuccess(true);
        setMessage(
          "Reset request successful, if there is a user associated with this email you will receive an email with instructions on how to reset your password."
        );
      }
    } catch (error: any) {
      if (error.response.status === 404) {
        setErrorMessage("No user found for specified email. Please sign up.");
        setIsValid(false);
      } else {
        setIsValid(false);
        setErrorMessage(
          "Internal server error. Please try again later. If the issue persists please contact support@event-spy.com"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    setIsValid(true);
    setErrorMessage("");
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      slotProps={{
        paper: {
          component: "form",
          onSubmit: (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            handleClose();
          },
          sx: { backgroundImage: "none" },
        },
      }}
    >
      <DialogTitle>Reset password</DialogTitle>
      {!success ? (
        <div>
          <DialogContent
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              width: "100%",
            }}
          >
            <DialogContentText>
              Enter your account&apos;s email address, and we&apos;ll send you a
              link to reset your password.
            </DialogContentText>
            <OutlinedInput
              autoFocus
              required
              margin="dense"
              id="email"
              name="email"
              label="Email address"
              placeholder="Email address"
              type="email"
              fullWidth
              error={!isValid}
              onChange={handleEmailChange}
            />
            {!isValid && (
              <DialogContentText color="error">
                {errorMessage}
              </DialogContentText>
            )}
          </DialogContent>
          <DialogActions sx={{ pb: 3, px: 3 }}>
            {!loading && <Button onClick={handleClose}>Cancel</Button>}
            <Button
              variant="contained"
              type="submit"
              onClick={handleSubmit}
              disabled={loading}
              sx={{
                "&.Mui-disabled": {
                  color: "black",
                  opacity: 0.7,
                  backgroundColor: "primary.main",
                },
              }}
            >
              Continue
            </Button>
          </DialogActions>
        </div>
      ) : (
        <div>
          <DialogContent
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              width: "100%",
            }}
          >
            <DialogContentText>{message}</DialogContentText>
          </DialogContent>
          <DialogActions sx={{ pb: 3, px: 3 }}>
            <Button onClick={handleClose}>Back to Login</Button>
          </DialogActions>
        </div>
      )}
    </Dialog>
  );
}
